import { useRef, useState } from "react";
import { apiProducao, type AlteraTempoPcf } from "../lib/api";
import { type Usuario } from "../lib/auth";
import { MsgModal } from "../components/MsgModal";
import { useOrdenacao, ThOrdena } from "../hooks/useOrdenacao";
import { mascaraTempoHM } from "../lib/tempoHM";
import { Cabecalho, CampoPcf, DadosPcf } from "./ProducaoPcf";

// altera_tempo_etapa (form pcf_etapas) — "Alteração de Tempo das Etapas".
// Fonte: C:\MOVIDO PARA SERVIDOR\FORMSTEEL\altera_tempo_etapa\Debug\pcf_etapas.prc
//   LostFocus da PCF (L220-251) · clique na grade (L310-331) · ROTINA-ALTERAR (L168-183) ·
//   MOVER-CAMPOS (L543-551) · Limpar (L343-352) · Sair (L194).

type Etapa = NonNullable<AlteraTempoPcf["etapas"]>[number];
const tempoTxt = (t: number) => (t || 0).toFixed(2).replace(".", ",");

export function AlteraTempoEtapa({ usuario, onVoltar }: { usuario: Usuario; onVoltar: () => void }) {
  const [numpcf, setNumpcf] = useState(0);
  const [p, setP] = useState<AlteraTempoPcf | null>(null);
  const [sel, setSel] = useState<Etapa | null>(null);
  const [tempo, setTempo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [chave, setChave] = useState(0);
  const ord = useOrdenacao(p?.etapas, {
    pos: (e) => e.posicao, eta: (e) => e.descri, desc: (e) => e.dimensionais, car: (e) => e.caracteristicas, dur: (e) => e.tempoEtapa,
  });
  const refTempo = useRef<HTMLInputElement>(null);

  async function consultar(n: number) {
    const r = await apiProducao.alteraTempoConsultar(n);
    if (!r.ok) {
      setErro(r.erro ?? "PCF não encontrada!");
      return;
    }
    setNumpcf(n);
    setP(r);
    setSel(null);
    setTempo("");
    refTempo.current?.focus(); // Enter na PCF -> tempo (L207-208)
  }

  function selecionar(e: Etapa) {
    setSel(e);
    setTempo(tempoTxt(e.tempoEtapa));
    setTimeout(() => refTempo.current?.focus(), 0);
  }

  async function alterar() {
    const t = Number(tempo.replace(",", ".")) || 0;
    if (!t) {
      setErro("Tempo da etapa não informado!");
      refTempo.current?.focus();
      return;
    }
    if (!numpcf) return;
    const r = await apiProducao.alteraTempoGravar({ numpcf, posicao: sel?.posicao ?? 0, etapa: sel?.descri ?? "", tempo: t });
    if (!r.ok) return setErro(r.mensagem);
    // MONTAR-TABELA + LIMPAR-CAMPOS (L181-183)
    const r2 = await apiProducao.alteraTempoConsultar(numpcf);
    if (r2.ok) setP(r2);
    setSel(null);
    setTempo("");
    refTempo.current?.focus();
  }

  function limpar() {
    setNumpcf(0);
    setP(null);
    setSel(null);
    setTempo("");
    setChave((k) => k + 1);
  }

  return (
    <div className="app">
      <Cabecalho usuario={usuario} titulo="Alteração de Tempo das Etapas" sub="PCF" onVoltar={onVoltar} />
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <div className="pcf-form">
        <section className="pcf-sec">
          <div className="pcf-grid">
            <CampoPcf key={chave} onConsultar={(n) => void consultar(n)} />
          </div>
          {p && <DadosPcf p={{ nomcli: p.nomcli ?? "", tipo: p.tipo ?? "", numped: p.numped ?? 0, situac: "" }} semSituacao />}
          <div className="pcf-grid">
            <label className="pcf-campo pcf-lg">
              <span>Etapa</span>
              <input value={sel?.descri ?? ""} readOnly tabIndex={-1} />
            </label>
            <label className="pcf-campo pcf-lg">
              <span>Dimensões</span>
              <input value={sel?.dimensionais ?? ""} readOnly tabIndex={-1} />
            </label>
            <label className="pcf-campo pcf-lg">
              <span>Características</span>
              <input value={sel?.caracteristicas ?? ""} readOnly tabIndex={-1} />
            </label>
            <label className="pcf-campo pcf-sm">
              <span>Duração (hhhh,mm)</span>
              <input
                ref={refTempo}
                className="num"
                inputMode="decimal"
                value={tempo}
                onChange={(e) => setTempo(mascaraTempoHM(e.target.value, tempo))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void alterar(); // Enter no tempo = ROTINA-ALTERAR (L282-283)
                  }
                }}
              />
            </label>
          </div>
        </section>
        {p && (
          <section className="pcf-sec">
            <div className="tabela-wrap">
              <table className="tabela-pcfs">
                <thead>
                  <tr>
                    <ThOrdena ord={ord} col="pos" className="num">Posição</ThOrdena>
                    <ThOrdena ord={ord} col="eta">Etapa</ThOrdena>
                    <ThOrdena ord={ord} col="desc">Descrição</ThOrdena>
                    <ThOrdena ord={ord} col="car">Características</ThOrdena>
                    <ThOrdena ord={ord} col="dur" className="num">Duração</ThOrdena>
                  </tr>
                </thead>
                <tbody>
                  {(p.etapas ?? []).length === 0 && (
                    <tr>
                      <td colSpan={5} className="plan-vazio">Nenhuma etapa.</td>
                    </tr>
                  )}
                  {ord.linhas.map((e) => (
                    <tr
                      key={e.posicao}
                      className={"row-clickable" + (sel?.posicao === e.posicao ? " plan-sel" : "")}
                      onClick={() => selecionar(e)}
                    >
                      <td className="num">{e.posicao}</td>
                      <td>{e.descri}</td>
                      <td>{e.dimensionais}</td>
                      <td>{e.caracteristicas}</td>
                      <td className="num">{tempoTxt(e.tempoEtapa)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
        <div className="pcf-acoes">
          <button className="btn-acao" disabled={!p} onClick={() => void alterar()}>
            Alterar Etapa
          </button>
          <button className="btn-secundario" onClick={limpar}>
            Limpar
          </button>
          <span className="spacer" />
          <button className="btn-secundario" onClick={onVoltar}>
            Sair
          </button>
        </div>
      </div>
    </div>
  );
}
